import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState, useEffect, useRef, useCallback } from 'react';
import { ProtectedRoute } from '../../components/ProtectedRoute';
import { useAuth } from '../../hooks/useAuth';
import { can } from '../../lib/roles';
import { DEFAULT_COMPANY_ID } from '../../lib/config';
import { listCandidates, createCandidate, analyzeCV, analyzeCVByFileId, getCandidateCvStreamUrl, type CandidateDto } from '../../lib/recruitment';
import { uploadFile } from '../../lib/fileUpload';

export const Route = createFileRoute('/rekrut/candidates/')({
  component: CandidatesPage,
});

function CandidatesPage() {
  return (
    <ProtectedRoute minimumRole="manager">
      <CandidatesContent />
    </ProtectedRoute>
  );
}

// ─── Confidence badge ─────────────────────────────────────────────────────────

function ConfidenceBadge({ confidence }: { confidence?: number }) {
  if (confidence === undefined) return null;
  const pct = Math.round(confidence * 100);
  const color =
    pct >= 80 ? 'bg-green-50 text-green-700' :
    pct >= 60 ? 'bg-yellow-50 text-yellow-700' :
                'bg-stone-100 text-stone-500';
  return (
    <span className={`text-[10px] px-1 py-0.5 rounded font-mono ${color}`} title={`Confidence: ${pct}%`}>
      {pct}%
    </span>
  );
}

// ─── File drop zone component ──────────────────────────────────────────────────

function FileDropZone({
  onFileSelected,
  accept = '.pdf,.docx,.doc,.txt',
  disabled,
}: {
  onFileSelected: (file: File) => void;
  accept?: string;
  disabled?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragging(false);
      if (disabled) return;
      const file = e.dataTransfer.files[0];
      if (file) onFileSelected(file);
    },
    [disabled, onFileSelected],
  );

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      onClick={() => !disabled && inputRef.current?.click()}
      className={`
        border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors
        ${dragging ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'}
        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
      `}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        disabled={disabled}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onFileSelected(file);
          // Reset so same file can be re-selected
          e.target.value = '';
        }}
      />
      <div className="flex flex-col items-center gap-2">
        <svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
            d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
        <p className="text-sm text-gray-500">
          Drop CV file here, or <span className="text-blue-600 font-medium">browse</span>
        </p>
        <p className="text-xs text-gray-400">PDF, DOCX, DOC, or TXT · max 20 MB</p>
      </div>
    </div>
  );
}

// ─── Analyze CV modal ──────────────────────────────────────────────────────────

function AnalyzeCVModal({
  candidate,
  companyId,
  userId,
  onClose,
  onDone,
}: {
  candidate: CandidateDto;
  companyId: string;
  userId?: string;
  onClose: () => void;
  onDone: (updated: CandidateDto) => void;
}) {

  const [mode, setMode] = useState<'file' | 'paste'>('file');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [cvText, setCvText] = useState('');
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState('');

  const canSubmit = mode === 'file' ? !!selectedFile : cvText.trim().length >= 3;
  const isLoading = uploading || analyzing;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (mode === 'file' && selectedFile) {
      // Step 1: upload file to get fileId
      setUploading(true);
      const uploadRes = await uploadFile(
        {
          companyId,
          uploadedByUserId: userId,
          entityType: 'cv',
          entityId: candidate.id,
          file: selectedFile,
        },
      );
      setUploading(false);

      if (!uploadRes.success || !uploadRes.data?.file.id) {
        setError(uploadRes.error ?? 'File upload failed');
        return;
      }

      // Step 2: run LLM extraction using the stored fileId
      setAnalyzing(true);
      const analyzeRes = await analyzeCVByFileId({
        candidateId: candidate.id,
        companyId,
        fileId: uploadRes.data.file.id,
      });
      setAnalyzing(false);

      if (analyzeRes.success && analyzeRes.data?.candidate) {
        onDone(analyzeRes.data.candidate);
      } else {
        setError(analyzeRes.error ?? 'LLM analysis failed');
      }
    } else {
      // Paste path — direct text submission
      setAnalyzing(true);
      const res = await analyzeCV({ candidateId: candidate.id, companyId, rawText: cvText });
      setAnalyzing(false);
      if (res.success && res.data?.candidate) {
        onDone(res.data.candidate);
      } else {
        setError(res.error ?? 'Analysis failed');
      }
    }
  }

  const statusText = uploading ? 'Uploading file…' : analyzing ? 'Analyzing with LLM…' : mode === 'file' ? 'Analyze with LLM' : 'Analyze with LLM';

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div>
            <h2 className="font-semibold text-gray-800">Analyze CV — {candidate.name}</h2>
            <p className="text-xs text-gray-500 mt-0.5">LLM extracts competency levels grounded in your company dictionary</p>
          </div>
          <button type="button" onClick={onClose} disabled={isLoading} className="text-gray-400 hover:text-gray-600 text-lg disabled:opacity-30">✕</button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Mode toggle */}
          <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
            <button
              type="button"
              onClick={() => setMode('file')}
              className={`flex-1 text-xs py-1.5 rounded-md font-medium transition-colors ${mode === 'file' ? 'bg-white shadow text-gray-800' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Upload File
            </button>
            <button
              type="button"
              onClick={() => setMode('paste')}
              className={`flex-1 text-xs py-1.5 rounded-md font-medium transition-colors ${mode === 'paste' ? 'bg-white shadow text-gray-800' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Paste Text
            </button>
          </div>

          {mode === 'file' ? (
            <div className="space-y-2">
              {selectedFile ? (
                <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-xl">
                  <svg className="w-5 h-5 text-blue-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{selectedFile.name}</p>
                    <p className="text-xs text-gray-400">{(selectedFile.size / 1024).toFixed(0)} KB</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedFile(null)}
                    className="text-gray-400 hover:text-red-500 text-sm"
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <FileDropZone onFileSelected={setSelectedFile} disabled={isLoading} />
              )}
            </div>
          ) : (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">CV / Resume Text</label>
              <textarea
                rows={8}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-xs resize-y"
                placeholder="Paste full CV text here…"
                value={cvText}
                onChange={(e) => setCvText(e.target.value)}
                disabled={isLoading}
              />
              <p className="text-xs text-gray-400 mt-1">The LLM will read this text and map skills to your company's competency framework.</p>
            </div>
          )}

          {/* Loading progress indicator */}
          {isLoading && (
            <div className="space-y-1.5">
              <div className="w-full h-1 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-blue-500 rounded-full animate-pulse" style={{ width: analyzing ? '70%' : '30%' }} />
              </div>
              <p className="text-xs text-gray-400 text-center">
                {uploading ? 'Uploading and parsing file…' : 'LLM is extracting competencies…'}
              </p>
            </div>
          )}

          {error && <p className="text-xs text-red-600">{error}</p>}

          <div className="flex gap-3">
            <button type="button" onClick={onClose} disabled={isLoading} className="flex-1 px-4 py-2 text-sm border rounded-lg text-gray-600 disabled:opacity-40">
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !canSubmit}
              className="flex-1 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg disabled:opacity-50 font-medium"
            >
              {isLoading ? statusText : 'Analyze with LLM'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Main content ─────────────────────────────────────────────────────────────

function CandidatesContent() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const companyId = user?.companyId ?? DEFAULT_COMPANY_ID;
  const canManage = can(user?.role, 'manage:rekrut');

  const [candidates, setCandidates] = useState<CandidateDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', email: '' });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  // Analyze CV modal state
  const [analyzingCandidate, setAnalyzingCandidate] = useState<CandidateDto | null>(null);

  function handleViewCv(e: React.MouseEvent, candidate: CandidateDto) {
    e.stopPropagation();
    const url = getCandidateCvStreamUrl(candidate.id, companyId);
    if (!url) { alert('Not authenticated'); return; }
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  useEffect(() => {
    let cancelled = false;
    listCandidates(companyId).then((res) => {
      if (cancelled) return;
      setLoading(false);
      if (res.success && res.data) setCandidates(res.data.candidates);
    });
    return () => { cancelled = true; };
  }, [companyId]);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setCreating(true);
    setCreateError('');
    const res = await createCandidate({ companyId, name: form.name, email: form.email });
    setCreating(false);
    if (res.success && res.data) {
      setCandidates((prev) => [...prev, (res.data as any).candidate]);
      setShowCreate(false);
      setForm({ name: '', email: '' });
    } else {
      setCreateError(res.error ?? 'Failed to add candidate');
    }
  }

  function handleAnalyzeDone(updated: CandidateDto) {
    setCandidates((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
    setAnalyzingCandidate(null);
  }

  const filtered = candidates.filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.email.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <Link to={'/rekrut' as any} className="text-sm text-blue-600 hover:underline">← Back to Rekrut</Link>
          <h1 className="text-2xl font-bold text-gray-900 mt-2">Candidate Database</h1>
          <p className="text-sm text-gray-500 mt-1">{candidates.length} candidates</p>
        </div>
        {canManage && (
          <button type="button" onClick={() => setShowCreate(true)} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">
            + Add Candidate
          </button>
        )}
      </div>

      {/* Search */}
      <div className="mb-4">
        <input
          className="w-full sm:w-80 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Search by name or email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="space-y-2">{[1, 2, 3].map((i) => <div key={i} className="h-16 bg-gray-100 rounded-xl animate-pulse" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl border shadow-sm p-10 text-center">
          <p className="text-gray-400">{search ? 'No candidates match your search.' : 'No candidates yet.'}</p>
          {canManage && !search && (
            <button type="button" onClick={() => setShowCreate(true)} className="mt-3 text-sm text-blue-600 underline">Add the first candidate</button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-600 text-xs uppercase">Name</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 text-xs uppercase hidden sm:table-cell">Email</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 text-xs uppercase hidden md:table-cell">Competencies</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 text-xs uppercase">Added</th>
                {canManage && (
                  <th className="text-left px-4 py-3 font-medium text-gray-600 text-xs uppercase">Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y">
              {filtered.map((c) => (
                <tr
                  key={c.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate({ to: '/rekrut/candidates/$candidateId' as any, params: { candidateId: c.id } as any })}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-purple-100 flex items-center justify-center text-xs font-medium text-purple-700 shrink-0">
                        {c.name.charAt(0).toUpperCase()}
                      </div>
                      <span className="font-medium text-gray-800">{c.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-500 hidden sm:table-cell">{c.email}</td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    {c.competencyProfile.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {c.competencyProfile.slice(0, 4).map((comp) => (
                          <span key={comp.code} className="text-xs bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded flex items-center gap-1">
                            {comp.code} L{comp.level}
                            <ConfidenceBadge confidence={comp.confidence} />
                          </span>
                        ))}
                        {c.competencyProfile.length > 4 && (
                          <span className="text-xs text-gray-400">+{c.competencyProfile.length - 4}</span>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400 italic">No CV analyzed</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-xs">
                    {new Date(c.createdAt).toLocaleDateString()}
                  </td>
                  {canManage && (
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        {c.cvUrl ? (
                          <button
                            type="button"
                            onClick={(e) => handleViewCv(e, c)}
                            className="text-xs px-2.5 py-1 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors font-medium flex items-center gap-1"
                            title="View original CV file"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            CV
                          </button>
                        ) : (
                          <span className="text-xs px-2.5 py-1 text-gray-300 italic" title="No CV file uploaded">No CV</span>
                        )}
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setAnalyzingCandidate(c); }}
                          className="text-xs px-2.5 py-1 border border-blue-200 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors font-medium"
                          title="Extract competencies from CV using LLM"
                        >
                          Analyze CV
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Candidate modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <div>
                <h2 className="font-semibold text-gray-800">Add Candidate</h2>
                <p className="text-xs text-gray-500 mt-0.5">After adding, use "Analyze CV" to upload their resume</p>
              </div>
              <button type="button" onClick={() => setShowCreate(false)} className="text-gray-400 hover:text-gray-600 text-lg">✕</button>
            </div>
            <form onSubmit={handleCreate} className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Full Name</label>
                <input required className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                <input required type="email" className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
              </div>
              {createError && <p className="text-xs text-red-600">{createError}</p>}
              <div className="flex gap-3">
                <button type="button" onClick={() => setShowCreate(false)} className="flex-1 px-4 py-2 text-sm border rounded-lg text-gray-600">Cancel</button>
                <button type="submit" disabled={creating} className="flex-1 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg disabled:opacity-50">{creating ? 'Adding…' : 'Add Candidate'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Analyze CV modal */}
      {analyzingCandidate && (
        <AnalyzeCVModal
          candidate={analyzingCandidate}
          companyId={companyId}
          userId={user?.id}
          onClose={() => setAnalyzingCandidate(null)}
          onDone={handleAnalyzeDone}
        />
      )}
    </div>
  );
}
